// T-282 (D-362) — the live look's loader. `?dataset=live` makes the app fetch a dataset `tools/meta-pull.mjs`
// wrote from Jason's real account and hand it to the demo server, which serves it as a live, view-only account.
//
// The files live in a `live/` folder NEXT TO the app (`live/index.json` lists the accounts; each dataset is
// `live/<key>.live.json`), which the founder preview (`tools/preview-web.mjs`) fills from
// %LOCALAPPDATA%\Spendlight\live\ on every refresh. They are never part of a build or a snapshot: real spend
// is customer data and stays outside the repo. A demo build without that folder simply has no live look.
//
// Nothing here touches a Meta host: it fetches two static files from the app's own origin, nothing else.
//
// **T-287: the SHAPE, the parser, the error and the two URLs are `@sl/core`'s** (`api/liveIndex.ts`, 0.3.8).
// Both builds carried a copy of all five, held in step by nothing at all — the phone's own note (T-284) asked
// for the one home. What stays here is the FETCH, which is genuinely per-app: the web resolves against
// `document.baseURI` because it routes on the hash, the phone against its origin plus `EXPO_BASE_URL`, and a
// device has no base at all. Everything this module used to export is re-exported, so no caller moved.

import {
  LiveDatasetError,
  NO_LIVE_DATASET_HINT,
  liveFileUrl,
  liveIndexUrl,
  parseLiveIndex,
  pickLiveEntry,
  type LiveIndexEntry,
} from '@sl/core';

export {
  LIVE_FOLDER,
  LIVE_INDEX_FILE,
  LIVE_FILE_NAME,
  LiveDatasetError,
  NO_LIVE_DATASET_HINT,
  liveFileUrl,
  liveIndexUrl,
  parseLiveIndex,
  pickLiveEntry,
  type LiveIndex,
  type LiveIndexEntry,
} from '@sl/core';

type FetchLike = (
  url: string,
) => Promise<{ ok: boolean; status: number; json(): Promise<unknown> }>;

async function getJson(fetchImpl: FetchLike, url: string): Promise<unknown> {
  let res;
  try {
    res = await fetchImpl(url);
  } catch (error) {
    throw new LiveDatasetError(`${NO_LIVE_DATASET_HINT} (${url}: ${String(error)})`);
  }
  if (!res.ok) throw new LiveDatasetError(`${NO_LIVE_DATASET_HINT} (${url}: HTTP ${res.status})`);
  return res.json();
}

/**
 * T-283 — just the list, for the shell's account switch: what has been pulled beside this app, or an EMPTY list
 * when nothing has (a missing folder is the ordinary state of a demo build, not a failure). Everything a live
 * entry shows in the menu — its name, how old the pull is — is in here, so the switch never fetches a dataset
 * it is not about to serve; a 3 MB file is not a menu's business.
 */
export async function loadLiveIndex(
  options: { fetchImpl?: FetchLike; base?: string } = {},
): Promise<LiveIndexEntry[]> {
  const fetchImpl: FetchLike = options.fetchImpl ?? ((url) => fetch(url));
  const base = options.base ?? document.baseURI;
  try {
    return parseLiveIndex(await getJson(fetchImpl, liveIndexUrl(base))).accounts;
  } catch {
    return [];
  }
}

/**
 * The live dataset to serve: the first account of `live/index.json` (or the one whose key is `accountKey`) and
 * its file, both relative to `base` (the document's base URI — the build's `base` is `./`, so the folder sits
 * beside `index.html` wherever the build is served from).
 */
export async function loadLiveDataset(
  options: {
    fetchImpl?: FetchLike;
    base?: string;
    accountKey?: string | null;
  } = {},
): Promise<{ entry: LiveIndexEntry; dataset: unknown }> {
  const fetchImpl: FetchLike = options.fetchImpl ?? ((url) => fetch(url));
  const base = options.base ?? document.baseURI;
  const index = parseLiveIndex(await getJson(fetchImpl, liveIndexUrl(base)));
  const entry = pickLiveEntry(index, options.accountKey);
  if (!entry) throw new LiveDatasetError(NO_LIVE_DATASET_HINT);
  const dataset = await getJson(fetchImpl, liveFileUrl(base, entry));
  return { entry, dataset };
}
